import React from 'react';
import PropTypes from 'prop-types';
import { startOfMonth, format } from 'date-fns';
import Box from '@mui/material/Box';
import { Form, Field, useForm, useFormState } from 'react-final-form';
import { Redirect } from 'react-router-dom';

import AddressAutoCompleteInput from 'components/AddressAutoCompleteInput';
import AddressInput from 'components/AddressInput';
import DatePicker from 'catering/components/DatePicker';
import OrderSettingsDetailHeader from 'components/OrderSettingsDetailHeader';
import PrimaryCta from 'components/Buttons/PrimaryCta';
import PageLoadSpinner from 'components/PageLoadSpinner';
import Select from 'components/Select';
import Tabs from 'components/Tabs';
import ScrollBox from 'catering/components/Forms/ScrollBox';
import LocationMenuCard from 'catering/components/Forms/LocationMenuCard';
import { DATE_KEY_FORMAT } from 'catering/constants/dates';

import {
  getDateTimeFromFulfillmentDateAndTime,
  getFulfillmentDateKey
} from 'catering/helpers';
import {
  useCateringLocationDeliveryZoneFromAddress,
  useCateringLocationFulfillmentHours,
  useCateringLocationMenuAvailability
} from 'catering/hooks/query';

import {
  NOT_DELIVERABLE,
  NOT_DELIVERABLE_NO_PICKUP
} from 'constants/errorTypes';
import { fulfillmentOptions } from 'constants/fulfillmentOptions';
import { translateAddressFromComponents } from 'helpers/addressHelpers';
import { LOCATION_OFFLINE } from 'bento-ordering/locations/constants/paths';

const FIELDS = {
  fullDeliveryAddress: 'full_delivery_address',
  fulfillmentDate: 'fulfillment_date',
  fulfillmentTime: 'fulfillment_time',
  shippingStreetAddress2: 'shipping_street_address_2',
  fulfillmentMethod: 'catering_fulfillment_method'
};

function OrderFulfillmentSettingsFormContents({
  cateringLocation,
  deliveryAddress,
  firstAvailableFulfillmentHours,
  setDeliveryAddress,
  setFulfillmentMenuId,
  setTimeWindowIntervalInMinutes,
  fulfillmentMenuId
}) {
  const form = useForm();
  const { values: formState } = useFormState();

  const {
    availableFulfillmentOptions,
    location: { slug: locationSlug }
  } = cateringLocation;

  const initialStartDateForAllAvailableHoursQuery = getFulfillmentDateKey(
    startOfMonth(formState.fulfillment_date)
  );
  const [
    startDateForAllAvailableHoursQuery,
    setStartDateForAllAvailableHoursQuery
  ] = React.useState(initialStartDateForAllAvailableHoursQuery);

  const [addressValidationError, setAddressValidationError] = React.useState(
    null
  );

  const {
    isLoading: isFulfillmentHoursLoading,
    data: allAvailableFulfillmentHours,
    fetchNextPage: fetchNextFulfillmentHoursPage,
    isError: fulfillmentHoursFetchFailed
  } = useCateringLocationFulfillmentHours({
    locationSlug,
    startDate: startDateForAllAvailableHoursQuery,
    fulfillmentMethod: formState.catering_fulfillment_method,
    customQueryOptions: {
      enabled: !!locationSlug && !!startDateForAllAvailableHoursQuery
    }
  });

  const {
    isLoading: isValidatingAddress,
    isSuccess: isAddressDeliverable
  } = useCateringLocationDeliveryZoneFromAddress({
    locationSlug,
    queryParams: deliveryAddress,
    customQueryOptions: {
      enabled: !!deliveryAddress.shipping_google_place_id,
      onSuccess: () => setAddressValidationError(null),
      onError: () =>
        setAddressValidationError(
          cateringLocation.is_closed_for_pickup
            ? NOT_DELIVERABLE_NO_PICKUP
            : NOT_DELIVERABLE
        )
    }
  });

  const {
    isLoading: isLoadingMenuAvailability,
    data: allAvailableLocationMenus,
    isError: menuAvailabilityFetchFailed
  } = useCateringLocationMenuAvailability({ locationSlug });

  const handleAddressChange = (...args) => {
    const [deliveryAddress, { lat, lng }, googlePlaceId] = args;
    const parsedDeliveryAddress = translateAddressFromComponents(
      deliveryAddress,
      googlePlaceId
    );
    const addressWithCoordinates = {
      ...parsedDeliveryAddress,
      shipping_lat: lat,
      shipping_lng: lng
    };
    setDeliveryAddress(addressWithCoordinates);
  };

  const handleMonthChange = async firstDayOfMonth => {
    const dateKey = getFulfillmentDateKey(firstDayOfMonth);
    await fetchNextFulfillmentHoursPage({ pageParam: dateKey });
  };

  /**
   *
   * Whenever we change fulfillment method, date, or time, we need to
   * ensure that we update all of the following items together:
   *
   * - fulfillment menu
   * - fulfillment date
   * - fulfillment time
   * - time window interval in minutes
   *
   * this information is retrieved from 2 possible sources:
   *
   * matching object in allAvailableFulfillmentHours
   * matching object in firstAvailableFullfillmentHours
   *
   * default is to always refer to allAvailableFulfillmentHours
   *
   * exception is to use firstAvailableFulfillmentHours when changing
   * between fulfillment methods, as that is the only way to guarantee
   * that we pre-populate with a valid selection
   * (allAvailableFulfillmentHours only contains data for 1 month, and it's possible
   * that the restaurant has their schedule set up such that both fulfillment methods
   * aren't always available during the same months)
   *
   */
  const handleFulfillmentMethodChange = fulfillmentMethod => {
    const firstAvailableHour =
      firstAvailableFulfillmentHours[fulfillmentMethod];
    const startDate = firstAvailableHour.fulfillment_date;

    // this will trigger a new api call to update the value
    // of allAvailableFulfillmentHours using this method and date
    // via useCateringLocationFulfillmentHours()
    form.change(FIELDS.fulfillmentMethod, fulfillmentMethod);
    setStartDateForAllAvailableHoursQuery(startDate);

    // update local state values for date, time, menu, and time window interval based on
    // the first available hour - we dont read from allAvailableHours here
    // because that object is updated asynchronously, and it may not contain values for this date yet
    const {
      catering_fulfillment_menu_id,
      catering_fulfillment_time_window_in_minutes,
      fulfillment_date,
      fulfillment_time
    } = firstAvailableHour;

    const fulfillmentDateTime = getDateTimeFromFulfillmentDateAndTime(
      fulfillment_date,
      fulfillment_time
    );
    form.change(FIELDS.fulfillmentDate, fulfillmentDateTime);
    form.change(FIELDS.fulfillmentTime, fulfillment_time);

    setFulfillmentMenuAndTimeWindowInterval(
      catering_fulfillment_menu_id,
      catering_fulfillment_time_window_in_minutes
    );
  };

  const handleMenuCardClick = async (date, time, menuId) => {
    const formattedDate = getFulfillmentDateKey(date, DATE_KEY_FORMAT);
    const firstOfMonth = startOfMonth(date);
    const formattedFirstOfMonth = getFulfillmentDateKey(
      firstOfMonth,
      DATE_KEY_FORMAT
    );
    if (!allAvailableFulfillmentHours[formattedDate]) {
      await fetchNextFulfillmentHoursPage({ pageParam: formattedFirstOfMonth });
    }
    form.change(FIELDS.fulfillmentDate, date);
    form.change(FIELDS.fulfillmentTime, time);
    const timeWindowsIntervalInMinutes = getTimeIntervals();
    setFulfillmentMenuAndTimeWindowInterval(
      menuId,
      timeWindowsIntervalInMinutes
    );
  };

  const handleDateChange = date => {
    form.change(FIELDS.fulfillmentDate, date);

    // when the user selects a new date,
    // prepopulate the time input with the first available time for that day
    const firstTimeOptionForThisDay =
      allAvailableFulfillmentHours[getFulfillmentDateKey(date)][0];

    handleTimeChange(firstTimeOptionForThisDay.value);
  };

  const handleTimeChange = time => {
    const { fulfillment_date } = form.getState().values;
    const allAvailableTimes =
      allAvailableFulfillmentHours[getFulfillmentDateKey(fulfillment_date)];
    const availableMenu = allAvailableTimes.find(t => t.value === time);
    const { fulfillmentMenuId, timeWindowsIntervalInMinutes } = availableMenu;

    setFulfillmentMenuAndTimeWindowInterval(
      fulfillmentMenuId,
      timeWindowsIntervalInMinutes
    );
    form.change(FIELDS.fulfillmentTime, time);
  };

  const setFulfillmentMenuAndTimeWindowInterval = (
    fulfillmentMenuId,
    timeWindowsIntervalInMinutes
  ) => {
    setFulfillmentMenuId(fulfillmentMenuId);
    setTimeWindowIntervalInMinutes(timeWindowsIntervalInMinutes);
  };

  const getTimeIntervals = () => {
    const { fulfillment_date, fulfillment_time } = form.getState().values;
    const date = format(fulfillment_date, DATE_KEY_FORMAT);
    const selectedDate = allAvailableFulfillmentHours[date];
    if (!selectedDate) {
      return;
    }
    const selectedTime = selectedDate.find(
      time => time.value === fulfillment_time
    );
    if (selectedTime) {
      const { timeWindowsIntervalInMinutes } = selectedTime;
      return timeWindowsIntervalInMinutes;
    }
  };

  const handleShouldDisableDate = date =>
    !allAvailableFulfillmentHours[getFulfillmentDateKey(date)];

  if (fulfillmentHoursFetchFailed || menuAvailabilityFetchFailed) {
    return <Redirect to={LOCATION_OFFLINE} />;
  }

  if (isFulfillmentHoursLoading || isLoadingMenuAvailability) {
    return <PageLoadSpinner />;
  }

  const availableLocationMenus =
    allAvailableLocationMenus[formState.catering_fulfillment_method] || [];

  const shouldDisplayScrollBox = availableLocationMenus.length > 1;

  const isDelivery =
    formState.catering_fulfillment_method === fulfillmentOptions.DELIVERY;

  const fulfillmentDateKey = getFulfillmentDateKey(formState.fulfillment_date);

  const fulfillmentTimeOptions = allAvailableFulfillmentHours[
    fulfillmentDateKey
  ]
    ? allAvailableFulfillmentHours[fulfillmentDateKey]
        .filter(
          (hour, index, self) =>
            index ===
            self.findIndex(
              t => hour.value === t.value && hour.label === t.label
            )
        )
        .map(({ label, value }) => ({
          label,
          value
        }))
    : [];

  const areFulfillmentSettingsValid = () => {
    const isFulfillmentTimeSet =
      !!formState.fulfillment_date && !!formState.fulfillment_time;

    if (isDelivery) {
      const isAddressSet = !!deliveryAddress.shipping_google_place_id;

      return (
        !isValidatingAddress &&
        isAddressSet &&
        isAddressDeliverable &&
        isFulfillmentTimeSet
      );
    }

    return isFulfillmentTimeSet;
  };

  const getHeading = () => {
    const headingName = shouldDisplayScrollBox ? 'Menus' : 'Order';
    return `${headingName} for ${formState.catering_fulfillment_method}`;
  };

  const isFormValid = areFulfillmentSettingsValid();
  const submitLabelText = `Schedule ${formState.catering_fulfillment_method}`;
  const selectedIdx =
    availableLocationMenus &&
    availableLocationMenus
      .map(({ menu_id }) => menu_id)
      .indexOf(fulfillmentMenuId);

  return (
    <Box paddingLeft={2.5} paddingRight={2.5}>
      <Field name="catering_fulfillment_method">
        {({ input }) => (
          <Tabs
            {...input}
            activeTab={availableFulfillmentOptions.indexOf(input.value)}
            handleTabClick={handleFulfillmentMethodChange}
            tabLabels={availableFulfillmentOptions}
          />
        )}
      </Field>

      <Box paddingTop={2.5} paddingBottom={2.5}>
        <Box marginBottom={2}>
          <OrderSettingsDetailHeader title={getHeading()} />
        </Box>

        {shouldDisplayScrollBox && (
          <Box marginBottom={2}>
            <ScrollBox selectedIdx={selectedIdx}>
              {availableLocationMenus.map((locationMenu, idx, arr) => (
                <LocationMenuCard
                  key={locationMenu.menu_id}
                  menuName={locationMenu.menu_name}
                  startDate={locationMenu.first_available_date}
                  startTime={locationMenu.first_available_time}
                  isLast={idx === arr.length - 1}
                  selected={fulfillmentMenuId === locationMenu.menu_id}
                  menuId={locationMenu.menu_id}
                  handleDateTimeChange={handleMenuCardClick}
                />
              ))}
            </ScrollBox>
          </Box>
        )}

        <Box marginBottom={2}>
          <Field name={FIELDS.fulfillmentDate}>
            {({ input }) => (
              <DatePicker
                label="Date"
                value={input.value}
                onChange={handleDateChange}
                onMonthChange={handleMonthChange}
                shouldDisableDate={handleShouldDisableDate}
              />
            )}
          </Field>
        </Box>

        <Field marginBottom={2} name={FIELDS.fulfillmentTime}>
          {({ input }) => (
            <Select
              label="Time"
              onChange={e => {
                handleTimeChange(e.target.value);
              }}
              value={input.value}
              options={fulfillmentTimeOptions}
              fullWidth
              dataTestId="time-picker"
            />
          )}
        </Field>

        {isDelivery && (
          <Box marginBottom={2.5}>
            <Box marginBottom={2}>
              <OrderSettingsDetailHeader title={'Delivery Address'} />
            </Box>
            <Box marginBottom={2}>
              <Field name={FIELDS.fullDeliveryAddress}>
                {({ input }) => (
                  <AddressAutoCompleteInput
                    initialValue={input.value}
                    handleSelect={handleAddressChange}
                    error={addressValidationError}
                  />
                )}
              </Field>
            </Box>

            <Field name={FIELDS.shippingStreetAddress2}>
              {({ input }) => (
                <AddressInput
                  type="text"
                  value={input.value}
                  label="Apt/Floor/Suite (optional)"
                  onChange={e => input.onChange(e)}
                  data-testid="address-2-input"
                />
              )}
            </Field>
          </Box>
        )}

        <PrimaryCta
          fullWidth
          type="submit"
          dataTestId="order-settings-submit-button"
          label={submitLabelText}
          disabled={!isFormValid}
        />
      </Box>
    </Box>
  );
}

OrderFulfillmentSettingsFormContents.propTypes = {
  cateringLocation: PropTypes.shape({
    availableFulfillmentOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
    is_closed_for_pickup: PropTypes.bool.isRequired,
    location: PropTypes.shape({
      slug: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  deliveryAddress: PropTypes.shape({
    shipping_street_address_1: PropTypes.string,
    shipping_street_address_2: PropTypes.string,
    shipping_city: PropTypes.string,
    shipping_state: PropTypes.string,
    shipping_postal_code: PropTypes.string,
    shipping_country: PropTypes.string,
    shipping_google_place_id: PropTypes.string
  }).isRequired,
  firstAvailableFulfillmentHours: PropTypes.object.isRequired,
  fulfillmentMenuId: PropTypes.number.isRequired,
  setDeliveryAddress: PropTypes.func.isRequired,
  setFulfillmentMenuId: PropTypes.func.isRequired,
  setTimeWindowIntervalInMinutes: PropTypes.func.isRequired
};

function OrderFulfillmentSettingsForm({ onSubmit, initialValues, ...rest }) {
  return (
    <Form onSubmit={onSubmit} initialValues={initialValues}>
      {({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <OrderFulfillmentSettingsFormContents {...rest} />
        </form>
      )}
    </Form>
  );
}

OrderFulfillmentSettingsForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default OrderFulfillmentSettingsForm;
